import { IdleTimeoutModal } from '@dxb-web-ui-framework/iam/components/IdleTimeoutModal';
import { SessionExpirationModal } from '@dxb-web-ui-framework/iam/components/SessionExpirationModal';
import {
  AppAuthenticationProvider,
  AppAuthenticationState,
} from '@dxb-web-ui-framework/iam/contexts/AppAuthentication';
import { AccessControlServiceImpl } from '@dxb-web-ui-framework/iam/domain/AccessControl/usecases';
import React, { FC, useState, useRef, useMemo, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

export type AppAuthenticationWrapperProps = {
  data?: AppAuthenticationState;
  accessControlService?: AccessControlServiceImpl;
  modalNotificationMaxExpirationTime?: number;
  modalNotificationExpirationTime?: number;
  loaderComponent: JSX.Element;
  errorComponent: (code?: string) => ReactNode;
};

const AppAuthenticationWrapper: FC<AppAuthenticationWrapperProps> = ({
  data,
  children,
  errorComponent,
  loaderComponent,
  accessControlService,
  modalNotificationExpirationTime = 1000 * 60 * 2,
  modalNotificationMaxExpirationTime = 1000 * 60 * 5,
}) => {
  // Hooks
  const { t } = useTranslation();
  // State
  const [showIdleModal, setShowIdleModal] = useState(false);
  const [showExpirationModal, setShowExpirationModal] = useState(false);
  // Refs
  const idleTimeoutModalRef = useRef<any>(null);
  const expirationTimeoutModalRef = useRef<any>(null);
  const appAuthenticationProviderRef = useRef<any>(null);
  // Memos
  const timeToModalClose = useMemo(
    () => modalNotificationExpirationTime / (60 * 1000),
    [modalNotificationExpirationTime],
  );

  return (
    <AppAuthenticationProvider
      ref={appAuthenticationProviderRef}
      modalRefs={{
        idleTimeoutModalRef,
        expirationTimeoutModalRef,
      }}
      modalSetters={{
        setShowIdleModal,
        setShowExpirationModal,
      }}
      data={data}
      errorComponent={errorComponent}
      loaderComponent={loaderComponent}
      accessControlService={accessControlService}
      modalNotificationExpirationTime={modalNotificationExpirationTime}
      modalNotificationMaxExpirationTime={modalNotificationMaxExpirationTime}>
      <IdleTimeoutModal
        onClose={appAuthenticationProviderRef.current?.onStay}
        onLogout={appAuthenticationProviderRef.current?.onLogout}
        ref={idleTimeoutModalRef}
        showModal={showIdleModal}
        timeLeft={timeToModalClose}
        wordings={{
          title: t('IdleTimeoutModal.DialogTitle'),
          content: t('IdleTimeoutModal.DialogContent.DialogContentText', {
            timeLeft: timeToModalClose,
            plural: timeToModalClose > 1 ? 's' : null,
          }),
          actionStay: t('IdleTimeoutModal.DialogActions.Button.Stay'),
          actionLogout: t('IdleTimeoutModal.DialogActions.Button.Logout'),
        }}
      />
      <SessionExpirationModal
        onClose={appAuthenticationProviderRef.current?.onClose}
        onLogout={appAuthenticationProviderRef.current?.onLogout}
        wordings={{
          title: t('SessionExpirationModal.DialogTitle'),
          content: t('SessionExpirationModal.DialogContent.DialogContentText', {
            timeLeft: timeToModalClose,
            plural: timeToModalClose > 1 ? 's' : null,
          }),
          action: t('SessionExpirationModal.DialogActions.Button.Close'),
          actionLogout: t('IdleTimeoutModal.DialogActions.Button.Logout'),
        }}
        shouldShowLogoutButton
        timeLeft={timeToModalClose}
        ref={expirationTimeoutModalRef}
        showModal={showExpirationModal}
      />
      {children}
    </AppAuthenticationProvider>
  );
};

export { AppAuthenticationWrapper };
